import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { environment } from "../../../environment";
import { MenuItemsAnalyticsResponse } from "../types";
import { buildAnalyticsParams } from "./buildAnalyticsParams";
import { AnalyticsFilterState } from "./useAnalyticsFilters";

const ENDPOINT = `${environment.VITE_URL_SCHEME}${environment.VITE_SERVER_DOMAIN}/api/v1/analytics/menu-items`;

const buildParams = (f: AnalyticsFilterState): Record<string, string> => ({
  ...buildAnalyticsParams(f),
  page: String(f.page),
  pageSize: String(f.pageSize),
});

export interface UseMenuItemsAnalytics {
  data: MenuItemsAnalyticsResponse | null;
  loading: boolean;
  error: boolean;
}

// Fetches menu-items analytics for the given filters. Re-fetches whenever the
// serialized params change; stale responses are discarded (last-request-wins).
// For custom ranges we wait until both dates are set before firing.
export const useMenuItemsAnalytics = (filters: AnalyticsFilterState): UseMenuItemsAnalytics => {
  const [data, setData] = useState<MenuItemsAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const requestIdRef = useRef(0);

  const params = buildParams(filters);
  const paramsKey = JSON.stringify(params);
  const customIncomplete = filters.timeRange === "custom" && (!filters.startDate || !filters.endDate);

  useEffect(() => {
    if (customIncomplete) {
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    axios
      .get<MenuItemsAnalyticsResponse>(ENDPOINT, { params, withCredentials: true })
      .then(res => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setData(res.data);
        setLoading(false);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(true);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, customIncomplete]);

  return { data, loading, error };
};
